import { getAnalysisURL } from '@modules/bookmark/helpers';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  getDashboardLink,
  trackDashboardEvent,
} from '@modules/dashboard/helpers';
import {
  useShouldShowDashboardQuantifyAsTable,
  useUpsertDashboard,
} from '@modules/dashboard/hooks';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import {
  useIsAnomaliesTrendVisible,
  useIsSingleRecordQuery,
} from '@modules/quantify/hooks';
import { ListRounded, StackedLineChartRounded } from '@mui/icons-material';
import {
  Box,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  AnalysisViewFragment,
  DashboardAnalysisMetadataInput,
} from 'generated/graphql';
import { useGetUser } from 'v2/core/services/user';

interface DashboardSwitchViewMenuItemProps {
  analysis: AnalysisViewFragment;
  closeMenu: () => void;
}

export const DashboardSwitchViewMenuItem = ({
  analysis,
  closeMenu,
}: DashboardSwitchViewMenuItemProps) => {
  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();
  const { showSuccessNotification } = useNotificationContext();

  const isAnomaliesTrendVisible = useIsAnomaliesTrendVisible();
  const isSingleRecordQuery = useIsSingleRecordQuery();

  const shouldShowQuantifyTable = useShouldShowDashboardQuantifyAsTable(
    analysis.id,
  );

  const dispatch = useDashboardStore(({ dispatch }) => dispatch);
  const analysesMetadata = useDashboardStore(
    ({ metadata }) => metadata.analysesMetadata,
  );
  const currentAnalysisMetadata = analysesMetadata.find(
    (metadata) => metadata.id === analysis.id,
  );
  const shouldShowAnomaliesAsChart =
    currentAnalysisMetadata?.shouldShowAnomaliesAsChart;

  const dashboard = useDashboardStore(({ dashboard }) => dashboard);
  const { upsertDashboard } = useUpsertDashboard();

  const getUpdatedAnalysesMetadataForAnomalies =
    (): DashboardAnalysisMetadataInput[] => {
      return Boolean(currentAnalysisMetadata)
        ? analysesMetadata.map((metadata) =>
            metadata.id === analysis.id
              ? {
                  ...metadata,
                  shouldShowAnomaliesAsChart: !shouldShowAnomaliesAsChart,
                }
              : metadata,
          )
        : [
            ...analysesMetadata,
            {
              id: analysis.id,
              shouldShowAnomaliesAsChart: !shouldShowAnomaliesAsChart,
            },
          ];
    };

  const handleSwitchView = async () => {
    let updatedAnalysesMetadata: DashboardAnalysisMetadataInput[] = [
      ...analysesMetadata,
    ];

    if (
      isAnomaliesTrendVisible &&
      typeof shouldShowAnomaliesAsChart === 'boolean'
    ) {
      // To support dashboards that have 'shouldShowAnomaliesAsChart' metadata
      // TODO: Remove when we deprecate 'shouldShowAnomaliesAsChart' & migrate to `shouldShowAsTable` from BE
      updatedAnalysesMetadata = getUpdatedAnalysesMetadataForAnomalies();
    } else {
      updatedAnalysesMetadata = Boolean(currentAnalysisMetadata)
        ? analysesMetadata.map((metadata) =>
            metadata.id === analysis.id
              ? {
                  ...metadata,
                  shouldShowAsTable: !shouldShowQuantifyTable,
                }
              : metadata,
          )
        : [
            ...analysesMetadata,
            {
              id: analysis.id,
              shouldShowAsTable: !shouldShowQuantifyTable,
            },
          ];
    }

    dispatch({
      type: DashboardActionType.SET_METADATA,
      payload: {
        analysesMetadata: updatedAnalysesMetadata,
      },
    });

    closeMenu();

    if (dashboard?.editToken) {
      await upsertDashboard({
        dashboardProps: {
          metadataV2: {
            analysesMetadata: updatedAnalysesMetadata,
          },
        },
      });
      showSuccessNotification({
        contentComponent: 'Your changes have been saved successfully.',
        props: {},
      });
    }

    trackDashboardEvent({
      eventName: shouldShowAnomaliesAsChart
        ? 'Dashboard - Toggled To Table View'
        : 'Dashboard - Toggled To Chart View',
      dashboardLink: dashboard ? getDashboardLink(dashboard, tenantSlug) : '',
      analysisLink: getAnalysisURL(analysis, tenantSlug),
      isAnomaliesView: shouldShowAnomaliesAsChart ? true : undefined,
    });
  };

  return (
    <Tooltip
      arrow
      placement="left"
      title={
        isSingleRecordQuery
          ? 'Table view is disabled for the selected calculation in Quantify'
          : ''
      }
    >
      <Box component="span">
        <MenuItem onClick={handleSwitchView} disabled={isSingleRecordQuery}>
          <ListItemIcon>
            {!shouldShowQuantifyTable ? (
              <ListRounded fontSize="small" />
            ) : (
              <StackedLineChartRounded fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            Switch to {!shouldShowQuantifyTable ? 'Table' : 'Chart'} View
          </ListItemText>
        </MenuItem>
      </Box>
    </Tooltip>
  );
};
